// frontend/src/pages/content/MuralDeAvisos.tsx

import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext.tsx";
import api from "../../api.ts";
import Menu from "../../components/layout/Menu.tsx";
import Footer from "../../components/layout/Footer.tsx";
import NoticeModal from "../../components/forms/NoticeModal.tsx";
import ConfirmationModal from "../../components/ui/ConfirmationModal.tsx";
import EmptyState from "../../components/ui/EmptyState.tsx";
import CompanyFilter from "../../components/ui/CompanyFilter.tsx";
import { sanitizeHtml } from "../../utils/sanitize.ts";
import toast from "react-hot-toast";
import { FiEdit, FiTrash2 } from "react-icons/fi";
import LoadingSpinner from "../../components/ui/LoadingSpinner.tsx";

// ─── Interfaces ───────────────────────────────────────────────────────────────

interface Notice {
  id: number;
  message: string;
  created_at: string;
  company_id: number | null;
  creator_name?: string;
}

// Altura máxima (px) de um aviso colapsado antes de exibir o "Ler mais".
const COLLAPSE_MAX_HEIGHT = 260;

// Mensagem de aviso com colapso: avisos longos são cortados com um fade e um
// botão "Ler mais"/"Ler menos". O botão só aparece quando há conteúdo cortado.
const NoticeMessage: React.FC<{ html: string }> = ({ html }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [overflowing, setOverflowing] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (el) setOverflowing(el.scrollHeight > COLLAPSE_MAX_HEIGHT + 8);
  }, [html]);

  return (
    <>
      <div
        ref={ref}
        className={`notice-message ${
          overflowing && !expanded ? "notice-message--collapsed" : ""
        }`}
        dangerouslySetInnerHTML={{ __html: sanitizeHtml(html) }}
      />
      {overflowing && (
        <button
          type="button"
          className="link-button notice-readmore"
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? "Ler menos" : "Ler mais"}
        </button>
      )}
    </>
  );
};

// ─── Componente ───────────────────────────────────────────────────────────────

const MuralDeAvisos: React.FC = () => {
  const { user, loading, hasPermission } = useAuth();
  const navigate = useNavigate();

  const [company, setCompany] = useState<string>("all");

  // ─── Estado ─────────────────────────────────────────────────────────────────

  const [notices, setNotices] = useState<Notice[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Paginação
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const ITEMS_PER_PAGE = 10;

  // Modal de criação/edição
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [noticeToEdit, setNoticeToEdit] = useState<Notice | null>(null);

  // Modal de confirmação de exclusão
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<number | null>(null);

  // ─── Redirect se não autenticado ────────────────────────────────────────────

  useEffect(() => {
    if (!loading && !user) navigate("/");
  }, [user, loading, navigate]);

  // ─── Fetch de avisos ────────────────────────────────────────────────────────

  const fetchNotices = useCallback(async () => {
    if (!user) return;
    setIsLoading(true);
    try {
      const res = await api.get("/api/notices", {
        params: {
          company,
          page: currentPage,
          limit: ITEMS_PER_PAGE,
        },
      });

      // O backend agora retorna { notices, totalCount, totalPages, currentPage }
      setNotices(res.data.notices);
      setTotalCount(res.data.totalCount);
      setTotalPages(res.data.totalPages);
    } catch (err) {
      console.error("Erro ao buscar avisos:", err);
      toast.error("Não foi possível carregar os avisos.");
    } finally {
      setIsLoading(false);
    }
  }, [user, company, currentPage]);

  useEffect(() => {
    if (user) fetchNotices();
  }, [user, fetchNotices]);

  // Reset de página ao trocar de empresa
  useEffect(() => {
    setCurrentPage(1);
  }, [company]);

  // ─── Handlers de Modal ──────────────────────────────────────────────────────

  const handleOpenCreate = () => {
    setNoticeToEdit(null);
    setIsModalOpen(true);
  };

  const handleOpenEdit = (notice: Notice) => {
    setNoticeToEdit(notice);
    setIsModalOpen(true);
  };

  const handleDeleteClick = (id: number) => {
    setItemToDelete(id);
    setIsConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!itemToDelete) return;
    try {
      await api.delete(`/api/notices/${itemToDelete}`);
      toast.success("Aviso excluído com sucesso!");
      // Se estava na última página com 1 item, volta uma página
      if (notices.length === 1 && currentPage > 1) {
        setCurrentPage((p) => p - 1);
      } else {
        fetchNotices();
      }
    } catch (err) {
      toast.error("Erro ao excluir o aviso.");
    } finally {
      setIsConfirmOpen(false);
      setItemToDelete(null);
    }
  };

  // ─── Formatação de data ─────────────────────────────────────────────────────

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

  // ─── Render ─────────────────────────────────────────────────────────────────

  if (loading) return <LoadingSpinner />;
  if (!user) return null;

  const canManage = hasPermission("notices.manage");

  return (
    <div className="p-2">
      <Menu />

      <div className={`content-area document-center company-${company}`}>
        {/* ── Cabeçalho ── */}
        <div className="document-header">
          <div>
            <h2 className="content-title">Mural de Avisos</h2>
          </div>

          {canManage && (
            <button
              className="form-button form-button--add"
              onClick={handleOpenCreate}
            >
              + Adicionar Aviso
            </button>
          )}
        </div>

        <CompanyFilter value={company} onChange={setCompany} />

        {/* ── Contador de resultados ── */}
        {!isLoading && totalCount > 0 && (
          <p className="notice-results-count">
            {totalCount} aviso{totalCount !== 1 ? "s" : ""}
          </p>
        )}

        {/* ── Lista de Avisos ── */}
        {isLoading ? (
          <LoadingSpinner variant="inline" label="Carregando avisos" />
        ) : notices.length > 0 ? (
          <div className="notice-list">
            {notices.map((notice) => (
              <div key={notice.id} className="notice-card">
                {/* Conteúdo HTML do aviso (Tiptap), com colapso "Ler mais" */}
                <NoticeMessage html={notice.message} />

                {/* Rodapé: data + ações */}
                <div className="notice-footer">
                  <small>
                    {/* NOVO: nome do autor + data */}
                    {notice.creator_name && (
                      <span className="notice-author-name">
                        {notice.creator_name}
                      </span>
                    )}
                    · {formatDate(notice.created_at)}
                  </small>

                  {canManage && (
                    <div className="notice-actions">
                      <button
                        className="list-button"
                        title="Editar aviso"
                        onClick={() => handleOpenEdit(notice)}
                      >
                        <FiEdit />
                      </button>
                      <button
                        className="delete-button"
                        title="Excluir aviso"
                        onClick={() => handleDeleteClick(notice.id)}
                      >
                        <FiTrash2 />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <EmptyState
            imageKey="avisos"
            title="Nenhum Aviso Publicado"
            message="Não há avisos publicados no momento. Verifique novamente mais tarde."
          />
        )}

        {/* ── Paginação ── */}
        {totalPages > 1 && (
          <div className="pagination-controls">
            <span>
              Página {currentPage} de {totalPages}
            </span>
            <div>
              <button
                className="list-button"
                onClick={() => setCurrentPage((p) => p - 1)}
                disabled={currentPage === 1}
              >
                Anterior
              </button>
              <button
                className="list-button"
                onClick={() => setCurrentPage((p) => p + 1)}
                disabled={currentPage === totalPages}
              >
                Próxima
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── Modal de Criação/Edição ── */}
      {isModalOpen && (
        <NoticeModal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setNoticeToEdit(null);
          }}
          onSuccess={() => {
            setIsModalOpen(false);
            setNoticeToEdit(null);
            fetchNotices();
          }}
          noticeToEdit={noticeToEdit}
          companySlug={company}
        />
      )}

      {/* ── Modal de Confirmação de Exclusão ── */}
      <ConfirmationModal
        isOpen={isConfirmOpen}
        onClose={() => {
          setIsConfirmOpen(false);
          setItemToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        title="Confirmar Exclusão"
        message="Tem certeza que deseja excluir este aviso? Esta ação não pode ser desfeita."
      />

      <Footer />
    </div>
  );
};

export default MuralDeAvisos;
